import { useModelSpend, modelSpendIsShowable, modelSpendMeter } from "../../state/modelSpend";
import { formatDayShort, formatGbp } from "../../usage/usageFormat";
import { cn } from "../../lib/utils";
import { Card } from "../ui/card";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/** The included fill is the workspace's Flash blue; the excess wears a status colour. */
const INCLUDED_FILL = "#6aa1d8";

/**
 * The allowance figures the card's copy reads. Both the short line and the
 * full sentence answer from this one shape, so they can never disagree about
 * which side of the allowance the month is on.
 */
export interface ModelSpendCopy {
  readonly state: "included" | "overage" | "blocked";
  readonly includedGbp: number;
  readonly remainingGbp: number | null;
  readonly overageGbp: number;
  readonly overageMultiplier: number;
  readonly overageChargeGbp: number;
  readonly onDemandEnabled: boolean;
  readonly paidLimitGbp?: number | undefined;
  readonly message?: string | null | undefined;
  readonly resetsOn: string;
}

/** "2×", "1.5×", "1.25×": the configured rate as billed, trailing zeros dropped. */
export function formatMultiplier(multiplier: number): string {
  return `${Number.parseFloat(multiplier.toFixed(2))}×`;
}

/**
 * The line under the meter: the figure that decides whether to read on, short
 * enough for the spend column. The reasoning behind it — the rate, the reset,
 * what happens once the allowance is gone — is a hover away in
 * `describeModelSpend`, so the card stays small without hiding anything.
 */
export function summariseModelSpend(status: ModelSpendCopy): string {
  const remaining = formatGbp(status.remainingGbp ?? 0);
  if (status.paidLimitGbp !== undefined) {
    if (!status.onDemandEnabled) return status.state === "blocked"
      ? "Usage paused · on demand off" : `${remaining} left · on demand off`;
    if (status.state === "blocked") return "Usage paused · limit reached";
    return status.state === "included" ? `${remaining} included remaining` : `${formatGbp(status.overageChargeGbp)} of ${formatGbp(status.paidLimitGbp)} paid limit`;
  }
  if (status.state === "included") {
    return status.onDemandEnabled
      ? `${remaining} left · then on demand at ${formatMultiplier(status.overageMultiplier)}`
      : `${remaining} left · on demand off`;
  }
  const over = `${formatGbp(status.overageGbp)} over`;
  return status.state === "blocked"
    ? `${over} · conversations paused`
    : `${over} · ${formatGbp(status.overageChargeGbp)} on demand`;
}

/** The sentence behind the meter, for each side of the allowance. */
export function describeModelSpend(status: ModelSpendCopy): string {
  const resets = `Resets ${formatDayShort(status.resetsOn)}.`;
  const rate = `${formatMultiplier(status.overageMultiplier)} the standard rate`;
  if (status.paidLimitGbp !== undefined && status.onDemandEnabled) {
    return `${status.message ?? `${formatGbp(status.remainingGbp ?? 0)} of your ${formatGbp(status.includedGbp)} included usage remains. Your monthly paid limit is ${formatGbp(status.paidLimitGbp)}; ${formatGbp(status.overageChargeGbp)} used.`} Ultrafast paid usage is charged at cost; other tiers use ${rate}. ${resets}`;
  }
  if (status.state === "included") {
    return status.onDemandEnabled
      ? `${formatGbp(status.remainingGbp ?? 0)} of your ${formatGbp(status.includedGbp)} included this month is left. Beyond it, usage is billed on demand at ${rate}. ${resets}`
      : `${formatGbp(status.remainingGbp ?? 0)} of your ${formatGbp(status.includedGbp)} included this month is left. On-demand usage is switched off for your account, so conversations pause once it is used. ${resets}`;
  }
  if (status.state === "blocked") {
    return `Your ${formatGbp(status.includedGbp)} included this month is used and on-demand usage is switched off for your account, so conversations are paused. Ask an organization admin to switch on-demand usage on. ${resets}`;
  }
  return `Your ${formatGbp(status.includedGbp)} included this month is used. ${formatGbp(status.overageGbp)} over is billed on demand at ${rate}: ${formatGbp(status.overageChargeGbp)}. ${resets}`;
}

/**
 * Usage page → the signed-in member's included monthly allowance, as a card in
 * the spend column under the tier shares.
 *
 * Within the allowance the track is the allowance and the fill is what is
 * used; past it the bar re-scales to the spend, the allowance keeps its
 * boundary, and the excess continues as a second segment separated by a
 * surface gap — amber while it is being billed on demand, red when the
 * admin has switched on-demand usage off and conversations are paused. The
 * figure is the server's (`/api/amoreg/model-spend`), always for the
 * current calendar month, whatever window the rest of the page shows.
 */
export function ModelSpendBar({ organizationId }: { readonly organizationId: string | null }) {
  const { status } = useModelSpend(organizationId);
  if (!modelSpendIsShowable(status)) return null;
  const meter = modelSpendMeter(status);
  if (meter === null) return null;

  const past = status.state === "overage" || status.state === "blocked";
  const blocked = status.state === "blocked";
  const nearlyUsed = !past && meter.usedShare >= 0.9;
  const includedWidth = `${(meter.includedShare * 100).toFixed(2)}%`;
  const usedWidth = `${(meter.usedShare * 100).toFixed(2)}%`;
  const overageWidth = `${(meter.overageShare * 100).toFixed(2)}%`;
  const excessClass = blocked ? "bg-error" : "bg-warning";
  const excessText = blocked ? "text-error" : "text-warning";

  return (
    <Card
      render={<section aria-labelledby="model-spend-heading" />}
      className={cn("gap-2.5 px-4 py-3.5", blocked && "border-error/32 bg-error-surface")}
    >
      <h2 id="model-spend-heading" className="text-xs tracking-wide text-muted-foreground uppercase">
        Included model usage · this month
      </h2>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xl font-semibold text-foreground tabular-nums">
          {formatGbp(Math.min(status.spentGbp, status.includedGbp))}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          of {formatGbp(status.includedGbp)}
        </span>
        {past ? (
          <span className={cn("text-xs font-medium tabular-nums", excessText)}>
            + {formatGbp(status.overageGbp)} {blocked ? "not available" : "on demand"}
          </span>
        ) : null}
      </div>

      {/* The meter. Inside the allowance: one track, one fill. Past it: the
          allowance track (now full) and the excess segment sharing one scale,
          so the boundary is where the money says it is. The 2px of surface
          between them is a border inside the excess segment's own width — a
          flex gap would shrink both segments off that scale. */}
      <div
        role="meter"
        aria-label="Your included model usage this month"
        aria-valuemin={0}
        aria-valuemax={status.includedGbp}
        aria-valuenow={Math.min(status.spentGbp, status.includedGbp)}
        aria-valuetext={`${formatGbp(status.spentGbp)} of ${formatGbp(status.includedGbp)} included${past ? `, ${formatGbp(status.overageGbp)} over` : ""}`}
        className="flex h-1.5 w-full items-stretch"
      >
        <div
          className="h-full overflow-hidden rounded-full bg-muted"
          style={{ width: includedWidth }}
          title={`${formatGbp(Math.min(status.spentGbp, status.includedGbp))} of ${formatGbp(status.includedGbp)} included`}
        >
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-500",
              nearlyUsed && "bg-warning",
            )}
            style={{
              width: usedWidth,
              ...(nearlyUsed ? {} : { backgroundColor: INCLUDED_FILL }),
            }}
          />
        </div>
        {past ? (
          <div
            className={cn("h-full rounded-full border-l-2 border-background", excessClass)}
            style={{ width: overageWidth }}
            title={
              blocked
                ? `${formatGbp(status.overageGbp)} over the allowance; usage is paused`
                : `${formatGbp(status.overageGbp)} over; paid usage ${formatGbp(status.overageChargeGbp)}`
            }
          />
        ) : null}
      </div>

      {/* The short line carries the decision; the whole explanation — rate,
          reset day, what happens at the boundary — is one hover or one tab
          stop away, so shrinking the card costs the reader nothing. */}
      <Tooltip>
        <TooltipTrigger
          className={cn(
            "w-fit text-left text-xs decoration-dotted underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
            blocked ? "font-medium text-error" : "text-muted-foreground",
          )}
        >
          {summariseModelSpend(status)}
        </TooltipTrigger>
        <TooltipPopup side="bottom" className="max-w-72 leading-tight whitespace-normal">
          {describeModelSpend(status)}
        </TooltipPopup>
      </Tooltip>
    </Card>
  );
}
